import React, { forwardRef } from 'react';
import classNames from 'classnames';
import ReactSelect, { Props as ReactSelectProps, StylesConfig, GroupBase } from 'react-select';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<ReactSelectProps<Option, IsMulti, Group>, 'styles'> {
  size?: 'sm' | 'md' | 'lg';
  invalid?: boolean;
}

const Select = forwardRef<any, SelectProps<any, boolean, GroupBase<any>>>(
  ({ className, size = 'md', invalid = false, ...rest }, ref) => {
    const sizeStyles = {
      sm: {
        control: { minHeight: '32px' },
        valueContainer: { padding: '0 8px' },
        input: { margin: '0' },
        indicatorSeparator: { margin: '4px 0' },
        dropdownIndicator: { padding: '4px' },
      },
      md: {
        control: { minHeight: '40px' },
        valueContainer: { padding: '2px 12px' },
        input: { margin: '0' },
        indicatorSeparator: { margin: '8px 0' },
        dropdownIndicator: { padding: '8px' },
      },
      lg: {
        control: { minHeight: '48px' },
        valueContainer: { padding: '4px 16px' },
        input: { margin: '0' },
        indicatorSeparator: { margin: '12px 0' },
        dropdownIndicator: { padding: '12px' },
      },
    };

    const customStyles: StylesConfig<any, boolean, GroupBase<any>> = {
      control: (provided, state) => ({
        ...provided,
        ...sizeStyles[size].control,
        backgroundColor: 'var(--ecme-input-bg, #fff)',
        borderColor: invalid
          ? 'var(--error, #ef4444)'
          : state.isFocused
          ? 'var(--primary, #6366f1)'
          : 'var(--ecme-input-border, #e5e7eb)',
        boxShadow: state.isFocused
          ? `0 0 0 2px ${invalid ? 'rgba(239, 68, 68, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`
          : 'none',
        '&:hover': {
          borderColor: invalid
            ? 'var(--error, #ef4444)'
            : state.isFocused
            ? 'var(--primary, #6366f1)'
            : 'var(--ecme-input-border-hover, #d1d5db)',
        },
        borderRadius: '0.5rem',
        transition: 'all 0.2s ease',
      }),
      valueContainer: (provided) => ({
        ...provided,
        ...sizeStyles[size].valueContainer,
      }),
      input: (provided) => ({
        ...provided,
        ...sizeStyles[size].input,
        color: 'var(--ecme-input-text, #1f2937)',
      }),
      singleValue: (provided) => ({
        ...provided,
        color: 'var(--ecme-input-text, #1f2937)',
      }),
      placeholder: (provided) => ({
        ...provided,
        color: 'var(--ecme-input-placeholder, #9ca3af)',
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        ...sizeStyles[size].indicatorSeparator,
        backgroundColor: 'var(--ecme-input-border, #e5e7eb)',
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        ...sizeStyles[size].dropdownIndicator,
        color: 'var(--ecme-input-placeholder, #9ca3af)',
        '&:hover': {
          color: 'var(--ecme-input-text, #1f2937)',
        },
      }),
      clearIndicator: (provided) => ({
        ...provided,
        padding: sizeStyles[size].dropdownIndicator.padding,
        color: 'var(--ecme-input-placeholder, #9ca3af)',
        '&:hover': {
          color: 'var(--error, #ef4444)',
        },
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: 'var(--ecme-dropdown-bg, #fff)',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid var(--ecme-input-border, #e5e7eb)',
        marginTop: '4px',
        zIndex: 50,
      }),
      // Only used with menuPortalTarget: above dialogs and sticky headers
      menuPortal: (provided) => ({
        ...provided,
        zIndex: 1060,
      }),
      menuList: (provided) => ({
        ...provided,
        padding: '4px',
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? 'var(--primary, #6366f1)'
          : state.isFocused
          ? 'var(--ecme-dropdown-hover, #f3f4f6)'
          : 'transparent',
        color: state.isSelected ? '#fff' : 'var(--ecme-input-text, #1f2937)',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        opacity: state.isDisabled ? 0.5 : 1,
        borderRadius: '0.375rem',
        padding: '8px 12px',
        '&:active': {
          backgroundColor: state.isSelected
            ? 'var(--primary, #6366f1)'
            : 'var(--ecme-dropdown-hover, #f3f4f6)',
        },
      }),
      multiValue: (provided) => ({
        ...provided,
        backgroundColor: 'var(--primary-lighter, #eef2ff)',
        borderRadius: '0.375rem',
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: 'var(--primary, #6366f1)',
        fontSize: '0.875rem',
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        color: 'var(--primary, #6366f1)',
        '&:hover': {
          backgroundColor: 'var(--primary, #6366f1)',
          color: '#fff',
        },
      }),
    };

    return (
      <ReactSelect
        ref={ref}
        className={classNames('ecme-select', className)}
        classNamePrefix="ecme-select"
        styles={customStyles}
        {...rest}
      />
    );
  }
);

Select.displayName = 'Select';

export default Select;
